import { editor, modes } from './cms/editor'
import actions from './cms/actions'
import layouts from './cms/layouts'
import { updatePage } from './cms/pages'

export async function main (platform, hostname, factory) {
  const api = create(platform, hostname, factory)

  platform.api.cms = api.cms
  platform.api.auth = api.auth

  if (IS_BROWSER) {
    platform.router.before(async ({ pathname }) => {
      // We don't want to try to check to redirect if the current URL is a 404, otherwise we could get into a redirect loop
      // For example, if a plugin requests /api/foo/bar, but /api/foo isn't set up on the server, then Node will pick up
      // the request instead of a normal API, in which case it's likely to be a 404 since the URL probably doesn't
      // exist as an actual page
      // If we checked for 2FA on that 404 page then it's possible the same plugin could make the same request again which
      // would start the infinite loop
      if (platform.api.core.isLoggedIn()) {
        const user = await platform.api.cms.users.find(platform.storeData.auth.claims.user)

        if (user.isOtpRequired && !user.hasOtp) {
          const config = await platform.api.cms.config.findAllAsObject()

          let otpSetupUrl = '/'

          if (config.otpSetupUrl && config.otpSetupUrl.value) {
            otpSetupUrl = config.otpSetupUrl.value
          }

          // Only redirect when we're not on the setup page to avoid infinite redirect loops
          if (pathname !== otpSetupUrl) {
            window.location = `${otpSetupUrl}?2fa=required`
          }
        }
      }
    })
  }

  platform.api.core.listen('component.load', ({ platform, data: { mod } }) => {
    if (mod.classNames?.includes('cms-tw') && !platform.storeData.tailwindLoaded) {
      platform.store.update(store => {
        store.tailwindLoaded = true

        return store
      })

      const footer = '<link rel="stylesheet" href="/tailwind.css">'

      if (IS_BROWSER) {
        const link = document.createElement('link')

        link.rel = 'stylesheet'
        link.href = '/tailwind.css'

        document.body.appendChild(link)
      } else {
        platform.api.server.footer.push(footer)
      }
    }
  })

  platform.store.update(store => {
    store.icons = {
      material: store.icons?.material || false,
    }

    store.userPreferences = {
      darkMode: false,
    }

    store.editor = {
      mode: modes.VIEW,
      editPluginInline: {
        id: null,
        component: null,
        instance: null,
        rangeData: null,
      },
      block: {},
      node: {
        id: null,
      },
      background: null,
      nodeFocus: null,
      lastWebsitePage: '/',
      showCMSMenu: false,
      sideBarMode: 'tools',
      actions: [],
      clientActions: [],
      onPageSaveFuncs: [],
      showTemplating: false,
      templatingTab: '',
      showBranchEditor: false,
      setInternalCss: null,
      cssExternalCss: null,
      setSharedCss: null,
      blog: {
        blogItem: {},
        hasBlogTitle: false,
      },
      files: {
        onFilesSelected: null,
        lastSelectedFolder: null,
      },
      images: {
        imageData: null,
        onImageUpdated: null,
      },
      lastPageTemplateId: '',
    }

    return store
  })
}

export function create (platform, hostname, factory) {
  let originalPluginFields = {}

  const api = {
    auth: {
      async checkScope (scope) {
        const res = await platform.fetch(`https://${hostname}/api/cms/tokens/scopes?scope=${scope}`)

        await factory.maybeThrowAPIError(res, '/api/cms/tokens/scopes')

        return res.json()
      },
      async login (email, password, totp) {
        const res = await platform.fetch(`https://${hostname}/api/cms/tokens`, {
          method: 'POST',
          body: JSON.stringify({
            email,
            password,
            otp: totp,
          }),
        })

        await factory.maybeThrowAPIError(res, '/api/cms/tokens')

        const data = await res.json()
        const { claims } = data.public

        platform.store.update(store => {
          store.auth.claims = claims

          return store
        })

        delete data.public.claims

        return data.public
      },
      async logout () {
        platform.store.update(store => {
          delete store.auth.claims

          return store
        })

        await platform.fetch(`https://${hostname}/api/cms/tokens`, {
          method: 'DELETE',
        })
      },
      async impersonate (email) {
        const res = await platform.fetch(`https://${hostname}/api/cms/tokens/impersonate`, {
          method: 'POST',
          body: JSON.stringify({
            email,
          }),
        })

        await factory.maybeThrowAPIError(res, '/api/cms/tokens/impersonate')

        const data = await res.json()
        const { claims } = data.public

        platform.store.update(store => {
          store.auth.claims = claims

          return store
        })

        delete data.public.claims

        window.location.reload()
      },
      async refreshToken (isFirstLoad) {
        if (!IS_BROWSER || !platform.storeData.auth.claims) {
          return
        }

        const res = await platform.fetch(`https://${hostname}/api/cms/tokens`, {
          method: 'POST',
          body: JSON.stringify({ refresh: true }),
        })

        // If we couldn't reach the API due to a gateway or timeout error then return so we don't log the user out yet
        // This is because their token may still be valid on the next try when the API might be up again
        // Any other response from the server that is not 200 OK considered enough to log the user out, even
        // a generic 500 error
        if ([502, 503, 504].includes(res.status)) {
          return
        }

        if (res.status !== 200) {
          await api.auth.logout()

          if (!isFirstLoad) {
            window.alert('Your session has expired so you have been automatically logged out.')
            window.location.reload()
          }

          return
        }

        const data = await res.json()
        const { claims } = data.public

        platform.store.update(store => {
          store.auth.claims = claims

          return store
        })
      },
      passwords: {
        async stats (password) {
          const func = factory.createCreate(platform, hostname, '/api/cms/passwords/stats')

          return func({ password })
        },
      },
      otp: {
        async create (key, data) {
          const func = factory.createCreate(platform, hostname, `/api/cms/users/${key}/otp`)

          return func(data)
        },
        async verify (key, data) {
          const res = await platform.fetch(`https://${hostname}/api/cms/users/${key}/otp`, {
            method: 'PATCH',
            body: JSON.stringify(data),
          })

          await factory.maybeThrowAPIError(res, `/api/cms/users/${key}/otp`)

          return res.json()
        },
        async remove (key, data) {
          const res = await platform.fetch(`https://${hostname}/api/cms/users/${key}/otp`, {
            method: 'DELETE',
            body: JSON.stringify(data),
          })

          await factory.maybeThrowAPIError(res, `/api/cms/users/${key}/otp`)

          return res.json()
        },
        async replaceScratchCodes (key, data) {
          const res = await platform.fetch(`https://${hostname}/api/cms/users/${key}/otp/scratch-codes`, {
            method: 'PUT',
            body: JSON.stringify(data),
          })

          await factory.maybeThrowAPIError(res, `/api/cms/users/${key}/otp/scratch-codes`)

          return res.json()
        },
        async sendReset (key, data) {
          const func = factory.createCreate(platform, hostname, `/api/cms/users/${key}/otp/reset`)

          return func(data)
        },
      },
    },
    cms: {
      logs: factory.createRoute(platform, hostname, '/api/cms/internal/logs'),
      backups: {
        ...factory.createRoute(platform, hostname, '/api/cms/backups'),
        async restore (file) {
          if (!file) {
            console.error('Backup restore: No file was supplied')

            return
          }

          if (typeof file === 'string') {
            await platform.fetch(`https://${hostname}/api/cms/backups/restore-from`, {
              method: 'PUT',
              body: JSON.stringify({ filename: file }),
            })
          } else {
            const formData = new window.FormData()

            formData.append('backup-archive', file)

            await platform.fetch(`https://${hostname}/api/cms/backups`, {
              method: 'PUT',
              body: formData,
            })
          }
        },
      },
      google: google(platform, hostname, factory.maybeThrowAPIError),
      emails: emails(platform, hostname, factory.maybeThrowAPIError),
      campaignMonitor: {
        async addSubscriberToList (campaignMonitorListKey, person) {
          try {
            const res = await platform.fetch(`https://${hostname}/api/cms/campaign-monitor?list-key=${campaignMonitorListKey}`, {
              method: 'POST',
              body: JSON.stringify(person),
            })
            return res.json()
          } catch (error) {
            console.error(error)
          }
        },
      },
      pages: {
        async updatePage (...args) {
          return updatePage(...args)
        },
        ...factory.createRoute(platform, hostname, '/api/cms/pages'),
        async render (url, blockId) {
          if (!url.includes('?')) {
            const params = platform.router.params.toString()

            if (params) {
              url += '?' + params
            }
          }

          let res = null

          if (!blockId) {
            res = await platform.fetch(`https://${hostname}/api/cms/pages/render?url=${encodeURIComponent(url)}`)
          } else {
            res = await platform.fetch(`https://${hostname}/api/cms/pages/render?url=${encodeURIComponent(url)}&block-id=${encodeURIComponent(blockId)}`)
          }

          return res.json()
        },
        async compile (scss) {
          const templateStyles = {
            inline: scss.inline || '',
            shared: scss.shared || '',
            external: scss.external || '',
          }

          try {
            const res = await platform.fetch(`https://${hostname}/api/legacy/resources/compile`, {
              method: 'POST',
              body: JSON.stringify(templateStyles),
            })

            return res.json()
          } catch (err) {
            console.error(err)
            return err
          }
        },
        async search (query, showNotInSitemap) {
          try {
            const res = await platform.fetch(`https://${hostname}/api/cms/pages/search?q=${query.trim()}&sm=${showNotInSitemap ? 'yes' : ''}`)

            return res.json()
          } catch (err) {
            console.error(err)
            return err
          }
        },
      },
      blocks: {
        ...factory.createRoute(platform, hostname, '/api/cms/blocks'),
        branches: {
          ...factory.createRoute(platform, hostname, '/api/cms/blocks/branches'),
        },
      },
      blogPosts: factory.createRoute(platform, hostname, '/api/cms/blog/posts'),
      blogCategories: {
        ...factory.createRoute(platform, hostname, '/api/cms/blog/categories'),
        async findAllPosts (key, options = {}) {
          const func = factory.createFindAll(platform, hostname, `/api/cms/blog/categories/${key}/posts`)

          return func(options)
        },
      },
      blogCategoryPosts: factory.createRoute(platform, hostname, '/api/cms/blog/categories/posts'),
      stats: {
        find: factory.createFind(platform, hostname, '/api/cms/stats'),
      },
      plugins: {
        ...factory.createRoute(platform, hostname, '/api/cms/plugins'),
        unused: {
          async findAll (named = false) {
            const res = await platform.fetch(`https://${hostname}/api/cms/plugins/unused?named=${named ? 'yes' : ''}`)

            await factory.maybeThrowAPIError(res, '/api/cms/plugins/unused')

            return res.json()
          },
          async removeAll (named = false) {
            const res = await platform.fetch(`https://${hostname}/api/cms/plugins/unused?named=${named ? 'yes' : ''}`, {
              method: 'DELETE',
            })

            await factory.maybeThrowAPIError(res, '/api/cms/plugins/unused')

            return res.json()
          },
        },
        findAllWithUrl: factory.createFindAllPages(platform, hostname, '/api/cms/plugins/pages'),
        async updatePlugin (pluginId, fields) {
          const originalFields = originalPluginFields[pluginId]

          if (!originalFields) {
            const originalCopy = platform.api.core.copyObject(platform.storeData.render.plugins[pluginId].fields)

            originalPluginFields[pluginId] = originalCopy
          }

          platform.store.update(store => {
            const plugin = store.render.plugins[pluginId]

            plugin.fields = fields

            if (platform.api.core.isEqual(originalFields, fields)) {
              store.editor.actions = store.editor.actions.filter(action => {
                const isPluginAction = action.name === 'PLUGIN_UPDATE' && action.meta.pluginId === pluginId

                return !isPluginAction
              })
            } else {
              store.editor.actions.push({
                name: 'PLUGIN_UPDATE',
                meta: {
                  pluginId,
                  pluginRef: plugin.ref,
                  fields,
                },
              })
            }

            return store
          })
        },
      },
      emailLogs: factory.createRoute(platform, hostname, '/api/cms/email/logs'),
      emailTemplates: {
        ...factory.createRoute(platform, hostname, '/api/cms/email/templates'),
        async preview (id, data) {
          const res = await platform.fetch(`https://${hostname}/api/cms/email/templates/${id}/preview`, {
            method: 'POST',
            body: JSON.stringify(data),
          })

          await factory.maybeThrowAPIError(res, `/api/cms/email/templates/${id}/preview`)

          return res.text()
        },
        async findAllByTypeName (typeName) {
          const typ = await api.cms.emailTemplateTypes.find({
            where: `emailTemplateType.name = ?`,
            whereArgs: [typeName],
          })

          return await api.cms.emailTemplates.findAllPages({
            where: `emailTemplate.emailTemplateTypeId = ?`,
            whereArgs: [typ.id],
            extract: true,
          })
        },
      },
      emailTemplateTypes: factory.createRoute(platform, hostname, '/api/cms/email/templates/types'),
      fonts: {
        ...factory.createRoute(platform, hostname, '/api/cms/fonts'),
        async googleFonts () {
          const res = await platform.fetch(`https://${hostname}/api/cms/fonts/google-fonts`)

          return res.json()
        },
      },
      linkedData: factory.createRoute(platform, hostname, '/api/cms/linked-data'),
      templates: factory.createRoute(platform, hostname, '/api/cms/templates'),
      config: (function () {
        const route = factory.createRoute(platform, hostname, '/api/cms/config')

        delete route.findAll

        const sections = factory.createFind(platform, hostname, '/api/cms/config')

        route.findSections = async function findSections () {
          return sections('sections')
        }

        route.findAllAsObject = async function findAllAsObject (options = {}) {
          const func = factory.createFindAllPages(platform, hostname, '/api/cms/config')
          const res = await func(options)
          const configResources = res.resources

          return configResources.reduce((acc, cur) => {
            acc[cur.entry] = cur

            return acc
          }, {})
        }

        return route
      })(),
      commands: {
        ...factory.createRoute(platform, hostname, '/api/cms/commands'),
        async saveActions (pageId, actions) {
          const res = await platform.fetch(`https://${hostname}/api/cms/commands/${pageId}`, {
            method: 'PUT',
            body: JSON.stringify({ actions }),
          })

          originalPluginFields = {}

          return res.json()
        },
        onPageSave (payload) {
          if (!payload.pluginId || typeof payload.func !== 'function') {
            return
          }

          platform.store.update(store => {
            for (let i = store.editor.onPageSaveFuncs.length - 1; i >= 0; i--) {
              if (store.editor.onPageSaveFuncs[i].pluginId === payload.pluginId) {
                delete store.editor.onPageSaveFuncs[i]
                store.editor.onPageSaveFuncs.splice(i, 1)
              }
            }

            store.editor.onPageSaveFuncs.push(payload)

            return store
          })
        },
      },
      users: {
        ...factory.createRoute(platform, hostname, '/api/cms/users'),
        async resetPassword (data = {}) {
          const res = await platform.fetch(`https://${hostname}/api/cms/users/reset-password`, {
            method: 'PATCH',
            body: JSON.stringify(data),
          })

          await factory.maybeThrowAPIError(res, '/api/cms/users/reset-password')

          return res.json()
        },
        async setup (data = {}) {
          const res = await platform.fetch(`https://${hostname}/api/cms/users/setup`, {
            method: 'POST',
            body: JSON.stringify(data),
          })

          await factory.maybeThrowAPIError(res, '/api/cms/users/setup')

          return res.json()
        },
        async setupUserData (setupToken) {
          const res = await platform.fetch(`https://${hostname}/api/cms/users/setup?setup-token=${setupToken}`)

          await factory.maybeThrowAPIError(res, '/api/cms/users/setup')

          return res.json()
        },
        async verifyEmail (data = {}) {
          const res = await platform.fetch(`https://${hostname}/api/cms/users/verify-email`, {
            method: 'PATCH',
            body: JSON.stringify(data),
          })

          await factory.maybeThrowAPIError(res, '/api/cms/users/verify-email')

          return res.json()
        },
        async sendTokenEmail (data = {}) {
          const res = await platform.fetch(`https://${hostname}/api/cms/users/token-email`, {
            method: 'POST',
            body: JSON.stringify(data),
          })

          await factory.maybeThrowAPIError(res, '/api/cms/users/token-email')

          return res.json()
        },
      },
      urlMaps: factory.createRoute(platform, hostname, '/api/cms/url-maps'),
      people: {
        ...factory.createRoute(platform, hostname, '/api/cms/people'),
        async createFlag (personId, data = {}) {
          const func = factory.createCreate(platform, hostname, `/api/cms/people/${personId}/flags`)

          return func(data)
        },
        async findAllFlags (personId, data = {}) {
          const func = factory.createFindAll(platform, hostname, `/api/cms/people/${personId}/flags`)

          return func(data)
        },
      },
      addresses: factory.createRoute(platform, hostname, '/api/cms/addresses'),
      countries: factory.createRoute(platform, hostname, '/api/cms/countries'),
      flags: factory.createRoute(platform, hostname, '/api/cms/flags'),
      flagPeople: factory.createRoute(platform, hostname, '/api/cms/flag-people'),
      profileData: factory.createRoute(platform, hostname, '/api/cms/profile-data'),
      profileSchemas: factory.createRoute(platform, hostname, '/api/cms/profile-schemas'),
      scopes: factory.createRoute(platform, hostname, '/api/cms/scopes'),
      trackers: factory.createRoute(platform, hostname, '/api/cms/trackers'),
      buckets: factory.createRoute(platform, hostname, '/api/cms/buckets'),
      calculator: {
        async calculate (data) {
          const res = await platform.fetch(`https://${hostname}/api/cms/calculator`, {
            method: 'POST',
            body: JSON.stringify(data),
          })

          await factory.maybeThrowAPIError(res, `/api/cms/calculator`)

          return res.json()
        },
      },
      entities: {
        counters: {
          async increment (name) {
            const res = await platform.fetch(`https://${hostname}/api/cms/entities/counters/${name}`, {
              method: 'POST',
            })

            await factory.maybeThrowAPIError(res, `/api/cms/entities/counters/${name}`)

            return res.json()
          },
        },
        unused: {
          async findAll () {
            const res = await platform.fetch(`https://${hostname}/api/cms/entities/unused`)

            await factory.maybeThrowAPIError(res, '/api/cms/entities/unused')

            return res.json()
          },
          async moveAll () {
            const res = await platform.fetch(`https://${hostname}/api/cms/entities/unused`, {
              method: 'POST',
            })

            await factory.maybeThrowAPIError(res, '/api/cms/files/unused')

            return res.json()
          },
        },
        ...factory.createRoute(platform, hostname, '/api/cms/entities'),
      },
      resources: {
        async removeCache () {
          const res = await platform.fetch(`https://${hostname}/api/resources/cache`, {
            method: 'DELETE',
          })

          await factory.maybeThrowAPIError(res, '/api/resources/cache')
        },
      },
      fileEntities: factory.createRoute(platform, hostname, '/api/cms/file-entities'),
      fileBuckets: factory.createRoute(platform, hostname, '/api/cms/file-buckets'),
      files: {
        ...factory.createRoute(platform, hostname, '/api/cms/files'),
        unused: {
          findAll: factory.createFindAll(platform, hostname, '/api/cms/files/unused'),
          async removeAll () {
            const res = await platform.fetch(`https://${hostname}/api/cms/files/unused`, {
              method: 'DELETE',
            })

            await factory.maybeThrowAPIError(res, '/api/cms/files/unused')

            return res.json()
          },
          async moveAll () {
            const res = await platform.fetch(`https://${hostname}/api/cms/files/unused`, {
              method: 'PUT',
            })

            await factory.maybeThrowAPIError(res, '/api/cms/files/unused')

            return res.json()
          },
        },
        config: {
          find: factory.createFind(platform, hostname, '/api/cms/__files/config'),
        },
        setOnFilesSelected (f) {
          platform.store.update(store => {
            store.editor.files.onFilesSelected = f

            return store
          })
        },
        async openFileManager () {
          platform.store.update(store => {
            store.openFileManager = true

            return store
          })
        },
        async closeFileManager () {
          platform.store.update(store => {
            store.openFileManager = false

            return store
          })
        },
        async getURLAsBlob (file) {
          try {
            const res = await platform.fetch(file)

            return res.blob()
          } catch (err) {
            console.error(err)
          }
        },
        async getURLAsArrayBuffer (url) {
          try {
            const res = await platform.fetch(url)

            return res.arrayBuffer()
          } catch (err) {
            console.error(err)
          }
        },
        async moveFiles (files, dir, name) {
          if (!Array.isArray(files)) {
            return Promise.reject(new Error('Files must be an array'))
          }

          if (typeof dir !== 'string') {
            return Promise.reject(new Error('dir must be a string'))
          }

          if (name === undefined) {
            name = ''
          }

          if (typeof name !== 'string') {
            return Promise.reject(new Error('name must be a string'))
          }

          const p = files.map(file => {
            return platform.api.cms.fileEntities.update({
              id: file.id,
              bucketName: dir,
              name,
            }).catch(err => err)
          })

          const responses = await Promise.all(p)

          return responses.map(res => res.data ? res.data : res)
        },
        async removeFiles (files) {
          if (!Array.isArray(files)) {
            return Promise.reject(new Error('files must be an array'))
          }

          const p = files.map(file => fetch(`https://${hostname}/api/cms/file-entities/${file.id}`, {
            method: 'DELETE',
          }).catch(err => err))
          const responses = await Promise.all(p)

          return responses.map(res => res.data ? res.data : res)
        },
        async removeFolders (folders) {
          if (!Array.isArray(folders)) {
            return Promise.reject(new Error('folders must be an array'))
          }

          const p = folders.map(folder => platform.api.cms.fileBuckets.remove(folder.name).catch(err => err))
          const responses = await Promise.all(p)

          return responses.map(res => res.data ? res.data : res)
        },
        async createFolder (folder) {
          folder = folderString(folder)

          if (!folder) {
            return Promise.reject(new Error('Folder name must not be empty'))
          }

          try {
            const res = await platform.api.cms.buckets.create({
              name: folder,
              IsPublic: true,
            })

            return res
          } catch (err) {
            console.error(err)
          }
        },
        async find (folder) {
          if (!folder) {
            folder = ''
          }

          folder = folder
            .replace(/\s+/g, ' ')
            .replace(/\/+/g, '/')
            .replace(/^\s+|\s+$/g, '')
            .replace(/^\/+|\/+$/g, '')
            .replace(/^\s+|\s+$/g, '')

          folder = folderString(folder)

          try {
            const res = await platform.fetch(`https://${hostname}/api/cms/file-entities/${folder}`)
            const data = await res.json()
            const processedFiles = platform.api.cms.files.processFiles(data.files, folder)

            const transientFolders = []

            for (const folder of data.folders) {
              const parts = folder.name.split('/').filter(part => part)

              for (let i = 0, l = parts.length; i < l; i++) {
                let folder = ''

                for (const part of parts) {
                  folder += `/${part}`
                }

                folder = folder.replace(/^\/|\/+$/g, '')

                parts.pop()

                if (folder === '') {
                  continue
                }

                if (!data.folders.some(f => f.name === folder) && !transientFolders.includes(folder)) {
                  transientFolders.push(folder)
                }
              }
            }

            for (const folder of transientFolders) {
              data.folders.push({
                name: folder,
                isTransient: true,
                isSystem: folder.includes('/$jolojo'),
              })
            }

            data.folders = data.folders.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))

            return {
              files: processedFiles,
              folders: platform.api.cms.files.processFolders(data.folders, folder),
            }
          } catch (err) {
            console.error(err)
          }
        },
        processFolders (folders, cwd) {
          return [...folders].map(folder => platform.api.cms.files.processFolder(folder, cwd))
        },
        processFolder (folder) {
          const rawPath = folder.name

          folder.metaData = folder.metaData || {}

          return {
            ...folder,
            path: {
              raw: rawPath,
              uri: window.encodeURI(rawPath),
            },
            label: folder.name.split('/').pop(),
            scopeWhitelist: folder.metaData.scopeWhitelist || [],
          }
        },
        processFiles (files, cwd) {
          return [...files].map(file => platform.api.cms.files.processFile(file, cwd))
        },
        processFile (file, cwd) {
          cwd = folderString(cwd)

          const { protocol, host } = window.location
          const fullDomain = protocol + '//' + host
          const rawPath = `/${cwd}/${file.name}`
          const ext = '.' + file.name.split('.').pop().toLowerCase()
          const encodedURI = window.encodeURI(rawPath)

          let mimeType = 'application/octet-stream'

          switch (ext) {
            case '.7z':
              mimeType = 'application/x-7z-compressed'
              break
            case '.css':
              mimeType = 'text/css'
              break
            case '.csv':
              mimeType = 'text/csv'
              break
            case '.doc':
              mimeType = 'application/msword'
              break
            case '.docx':
              mimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
              break
            case '.gif':
              mimeType = 'image/gif'
            case '.htm': // eslint-disable-line
            case '.html':
              mimeType = 'text/html'
              break
            case '.jpg': // eslint-disable-line
            case '.jpeg':
              mimeType = 'image/jpeg'
              break
            case '.js':
              mimeType = 'text/javascript'
              break
            case '.json':
              mimeType = 'application/json'
              break
            case '.pdf':
              mimeType = 'application/pdf'
              break
            case '.png':
              mimeType = 'image/png'
              break
            case '.svg':
              mimeType = 'image/svg+xml'
              break
            case '.txt':
              mimeType = 'text/plain'
              break
            case '.wav':
              mimeType = 'audio/wav'
              break
            case '.weba':
              mimeType = 'audio/webm'
              break
            case '.webm':
              mimeType = 'video/webm'
              break
            case '.webp':
              mimeType = 'image/webp'
              break
            case '.xls':
              mimeType = 'application/vnd.ms-excel'
              break
            case '.xlsx':
              mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
              break
            case '.xml':
              mimeType = 'application/xml'
              break
            case '.zip':
              mimeType = 'application/zip'
              break
          }

          if (file.metaData) {
            file.metaData.exif = file.metaData.exif || {}
          }

          return {
            ...file,
            ext,
            mimeType,
            path: {
              raw: rawPath,
              uri: encodedURI,
              abs: {
                raw: fullDomain + rawPath,
                uri: fullDomain + encodedURI,
              },
            },
            alt: file.metaData.alt || '',
            title: file.metaData.title || '',
            jpegQuality: file.metaData.jpegQuality,
            scopeWhitelist: file.metaData.scopeWhitelist || [],
          }
        },
        async upload (files, folder, options = {}) {
          if (files && !Array.isArray(files)) {
            files = Array.from(files)
          }

          if (!files || !files.length) {
            const message = 'Please select at least one file to upload'
            const err = new Error(message)

            err.data = {
              status: 204000,
              message,
            }

            return Promise.reject(err)
          }

          if (platform.storeData.isOnline) {
            const filesConfig = await platform.api.cms.files.config.find()

            for (const file of files) {
              const ext = `.${file.name.split('.').pop().toLowerCase()}`

              if (!filesConfig.accept.includes(ext)) {
                const message = `Uploading ${ext} files is not supported, please choose another file`
                const err = new Error(message)

                err.data = {
                  status: 204001,
                  message,
                }

                return Promise.reject(err)
              }
            }
          }

          const formData = new window.FormData()
          let data = {
            folder,
            success: {},
          }

          files.forEach(file => {
            if (!file.name) {
              console.error('A file did not have a name field', file)
              return
            }

            formData.append(file.name, file)

            data.success[file.name] = {
              name: file.name,
              folder,
            }
          })

          formData.append('folder', folder)
          formData.append('isPublic', !options.isPrivate)

          if (platform.storeData.isOnline) {
            const res = await platform.fetch(`https://${hostname}/api/cms/files`, {
              method: 'POST',
              body: formData,
            })

            data = await res.json()

            if (!data.success && !data.failed) {
              const err = new Error('Unexpected response')

              if (data.code) {
                err.data = {
                  status: res.status,
                  ...data,
                }
              }

              return Promise.reject(err)
            }

            if (data.failed) {
              for (const name in data.failed) {
                console.warn(`File: ${name} failed to upload`)
              }
            }
          } else {
            platform.store.update(store => {
              store.offlineActions.push(() => platform.api.cms.files.upload(files, folder, platform.api.core.copyObject(options)))

              return store
            })
          }

          for (const entity of Object.values(data.entities || {})) {
            entity.metaData = {
              ...entity.metaData,
              name: entity.name,
              folder: data.folder,
            }
          }

          if (platform.storeData.isOnline) {
            try {
              await platform.api.cms.resources.removeCache()
            } catch (err) {
              console.error(err)
            }
          }

          return {
            success: data.success || {},
            failed: data.failed || {},
            entities: data.entities || {},
          }
        },
      },
      images: {
        setImageData (data) {
          platform.store.update(store => {
            store.editor.images.imageData = data

            return store
          })
        },
        setOnImageUpdated (f) {
          platform.store.update(store => {
            store.editor.images.onImageUpdated = f

            return store
          })
        },
        openImageEditor () {
          platform.store.update(store => {
            store.editor.images.isOpen = true

            return store
          })
        },
        closeImageEditor () {
          platform.store.update(store => {
            store.editor.images.isOpen = false

            return store
          })
        },
      },
      nodes: {
        ...factory.createRoute(platform, hostname, '/api/cms/nodes'),
        findAllWithUrl: factory.createFindAllPages(platform, hostname, '/api/cms/nodes/pages'),
      },
      layouts: layouts(platform, hostname, factory),
      editor: editor(platform),
      actions: actions(platform),
      programs: {
        ...factory.createRoute(platform, hostname, '/api/cms/programs'),
        async findByIds (ids) {
          if (!Array.isArray(ids)) {
            ids = [ids]
          }

          return platform.api.cms.programs.find({
            where: `program.id IN (???) OR json_valid(program.custom) AND json_extract(program.custom, ?) IN (???)`,
            whereArgs: [ids, '$.pluginId', ids],
          })
        },
        async globals (type) {
          const res = await platform.fetch(`https://${hostname}/api/cms/programs/globals?type=${type}`)

          return res.json()
        },
        async execute (id, data) {
          const res = await platform.fetch(`https://${hostname}/api/cms/programs/${id}/execute`, {
            method: 'POST',
            body: JSON.stringify(data),
          })

          return res.json()
        },
      },
      programData: factory.createRoute(platform, hostname, '/api/cms/program-data'),
    },
  }

  if (IS_BROWSER) {
    api.auth.refreshToken(true)

    setInterval(api.auth.refreshToken, 1000 * 60 * 5)
  }

  return api
}

function folderString (folder) {
  if (!folder) {
    folder = ''
  }

  folder = folder
    .replace(/\s+/g, ' ')
    .replace(/\/+/g, '/')
    .replace(/^\s+|\s+$/g, '')
    .replace(/^\/+|\/+$/g, '')
    .replace(/^\s+|\s+$/g, '')

  return folder
}

function google (platform, hostname) {
  const publicAPI = {
    pageSpeedMobile,
    pageSpeedDesktop,
    authorise,
    recaptcha: {
      init: recaptchaInit,
      execute: recaptchaExecute,
      reset: recaptchaReset,
    },
  }

  async function pageSpeedMobile (...args) {
    const data = await pageSpeed('mobile', ...args)

    return data
  }

  async function pageSpeedDesktop (...args) {
    const data = await pageSpeed('desktop', ...args)

    return data
  }

  async function pageSpeed (strategy, speedURL, apiKey) {
    const q = `?url=${speedURL}&strategy=${strategy}&key=${apiKey}`
    const res = await platform.fetch(`https://www.googleapis.com/pagespeedonline/v2/runPagespeed${q}`)

    return res.data
  }

  async function authorise (code) {
    const res = await platform.fetch(`https://${hostname}/google/authorise`, { code }, { method: 'POST' })

    return res.data
  }

  let recaptchaLoaded = false

  async function recaptchaInit (size, sitekey, formId, submitCallback) {
    if (typeof window === 'undefined') {
      return Promise.reject(new Error('ReCaptcha must be loaded in a browser environment'))
    }

    if (!size) {
      return Promise.reject(new Error('ReCaptcha must be provided a size'))
    }

    if (!sitekey) {
      return Promise.reject(new Error('ReCaptcha must be provided a site key'))
    }

    if (!formId) {
      return Promise.reject(new Error('ReCaptcha must be provided a form ID'))
    }

    if (!submitCallback) {
      return Promise.reject(new Error('ReCaptcha must be provided a submit callback'))
    }

    return new Promise(resolve => {
      let s = document.getElementById('google-recaptcha-api-script')

      if (s) {
        recaptchaLoaded = true
      } else {
        s = document.createElement('script')

        s.id = 'google-recaptcha-api-script'
        s.src = platform.api.core.unsafe.trustedScriptURL('https://www.google.com/recaptcha/api.js?render=explicit')
        s.async = true
        s.defer = true
        s.onload = function () {
          recaptchaLoaded = true
        }

        document.head.appendChild(s)
      }

      let interval = setInterval(() => {
        if (!recaptchaLoaded || !document.getElementById(formId) || !window.grecaptcha || !window.grecaptcha.render) {
          return
        }

        clearInterval(interval)
        interval = null

        const widgetId = window.grecaptcha.render(formId, {
          sitekey,
          callback: submitCallback,
          size,
        })

        resolve(widgetId)
      }, 100)
    })
  }

  function recaptchaExecute (widgetId) {
    if (typeof window === 'undefined' || !window.grecaptcha) {
      return
    }

    window.grecaptcha.execute(widgetId)
  }

  function recaptchaReset (widgetId) {
    if (typeof window === 'undefined' || !window.grecaptcha) {
      return
    }

    window.grecaptcha.reset(widgetId)
  }

  return publicAPI
}

function emails (platform, hostname, maybeThrowAPIError) {
  const publicAPI = {
    send,
  }

  /*
  email -> [{
    allowLogging: true, // optional
    recaptcha: 'token-from-recaptcha',
    userId: 'v4-uuid', // optional
    url: '/page/url', // optional
    fromAddress: 'foo@bar.baz'
    fromName: 'Foo Bar',
    to: ['foo@bar.baz', 'bar@foo.baz'], // or string
    cc: ['foo@bar.baz', 'bar@foo.baz'], // or string; optional
    bcc: ['foo@bar.baz', 'bar@foo.baz'], // or string; optional
    replyTo: ['foo@bar.baz', 'bar@foo.baz'], // or string; optional
    template: {
      id: '123',
      vars: {}, // this can contain anything as long as it's compatible with the template
    },
    type: 'html' // html|plain -- html is default
    attachments: ['filename.txt', 'image.jpg'] // file manager paths or names of files from the `files` arg; optional
    crmData: {} // data to send to the CRM; requires allowLogging to be set to true; optional
  }]
  */
  async function send (emails, files) {
    if (!Array.isArray(emails)) {
      emails = [emails]
    }

    const formData = new window.FormData()

    if (Array.isArray(files)) {
      files.forEach(file => {
        if (!file.name) {
          console.error('A file did not have a name field', file)
          return
        }

        formData.append(file.name, file)
      })
    }

    for (let i = 0; i < emails.length; ++i) {
      const email = emails[i]

      email.allowLogging = !!email.allowLogging

      // if (!email.recaptcha) {
      //   return Promise.reject(new Error(`[${i}]: An email must specify a recaptcha value`))
      // }

      if (!email.template || !email.template.id) {
        return Promise.reject(new Error(`[${i}]: An email must specify a template.id value`))
      }

      if (!Array.isArray(email.to) && email.to) {
        email.to = [email.to]
      }
      if (!Array.isArray(email.cc) && email.cc) {
        email.cc = [email.cc]
      }
      if (!Array.isArray(email.bcc) && email.bcc) {
        email.bcc = [email.bcc]
      }
      if (!Array.isArray(email.replyTo) && email.replyTo) {
        email.replyTo = [email.replyTo]
      }
      if (!Array.isArray(email.attachments) && email.attachments) {
        email.attachments = [email.attachments]
      }

      email.type = email.type || 'html'
      if (email.type !== 'html' && email.type !== 'plain') {
        email.type = 'html'
      }
    }

    formData.append('emails', JSON.stringify(emails))

    const route = '/api/cms/emails'
    const res = await platform.fetch(`https://${hostname}${route}`, {
      method: 'POST',
      body: formData,
    })

    await maybeThrowAPIError(res, route)

    return res.json()
  }

  return publicAPI
}
